import { auth, functions } from './config';
import { db } from './config';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged, sendPasswordResetEmail } from 'firebase/auth';
import { doc, getDoc, setDoc, getDocs, collection } from 'firebase/firestore';
import { ref, computed } from 'vue';
import { httpsCallable } from 'firebase/functions';

const user = ref(null);
const loading = ref(true);
const userRole = ref(null);
const error = ref(null);

export const useAuth = () => {
  const isAuthenticated = computed(() => !!user.value);
  const isAdmin = computed(() => userRole.value === 'admin');
  const canEdit = computed(() => ['admin', 'editor'].includes(userRole.value));

  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      user.value = userCredential.user;
      return userCredential;
    } catch (error) {
      console.error('Login error:', error.message);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      user.value = null;
    } catch (error) {
      console.error('Logout error:', error.message);
      throw error;
    }
  };

  const loadUserRole = async (userId) => {
    try {
      const userDoc = await getDoc(doc(db, 'user_roles', userId));
      if (userDoc.exists()) {
        userRole.value = userDoc.data().role;
      } else {
        userRole.value = 'viewer';
      }
    } catch (error) {
      console.error('Error in loadUserRole:', error);
      userRole.value = 'viewer';
    }
  };

  const updateUserRole = async (userId, newRole, userData = {}) => {
    try {
      if (!isAdmin.value) throw new Error('Permission denied');
      await setDoc(doc(db, 'user_roles', userId), {
        role: newRole,
        name: userData.name,
        ...userData,
      }, { merge: true });
      
      return true;
    } catch (error) {
      console.error('Error updating user role:', error);
      throw error;
    }
  };

  const getAllUsers = async () => {
    try {
      if (!isAdmin.value) throw new Error('Permission denied');
      const userRoles = await getDocs(collection(db, 'user_roles'));
      return userRoles.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  };

  const removeUser = async (userId) => {
    try {
      if (!isAdmin.value) throw new Error('Permission denied');
      
      const deleteUserFunction = httpsCallable(functions, 'deleteUser');
      await deleteUserFunction({ userId });
      
      return true;
    } catch (error) {
      console.error('Error removing user:', error);
      throw error;
    }
  };

  const createNewUser = async (userData) => {
    try {
      if (!isAdmin.value) throw new Error('Permission denied');
      
      const createUserFunction = httpsCallable(functions, 'createUser');
      
      const result = await createUserFunction({
        email: userData.email,
        password: userData.password,
        name: userData.name,
        role: userData.role
      });

      return result.data;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  };

  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return true;
    } catch (err) {
      error.value = err.message;
      throw err;
    }
  };

  // Modify existing onAuthStateChanged
  onAuthStateChanged(auth, async (currentUser) => {
    user.value = currentUser;
    if (currentUser) {
      await loadUserRole(currentUser.uid);
    } else {
      userRole.value = null;
    }
    loading.value = false;
  });

  // Set loading to false after a timeout in case auth state doesn't change
  setTimeout(() => {
    loading.value = false;
  }, 1000);

  return {
    user,
    loading,
    userRole,
    isAuthenticated,
    isAdmin,
    canEdit,
    login,
    logout,
    updateUserRole,
    getAllUsers,
    removeUser,
    createNewUser,
    resetPassword
  };
};
